import type { Dictionary } from "./types";

export const zhHans = {
  common: {
    signIn: "登录",
    signOut: "退出登录",
    settings: "设置",
    help: "帮助",
    add: "新增",
    account: "账户",
    cancel: "取消",
    apply: "应用",
    dismiss: "关闭",
    retry: "重试",
    save: "保存",
    loading: "加载中",
    openWorkspace: "打开工作台",
    language: "语言",
    signInWith: (provider) => `使用 ${provider} 登录`
  },
  dataTable: {
    rows: (count) => `${count} 行`,
    columns: "列"
  },
  entities: {
    person: "人物",
    organisation: "公司",
    event: "事件",
    opportunity: "商机",
    note: "笔记",
    personSingular: "联系人",
    organisationSingular: "公司",
    eventSingular: "事件",
    opportunitySingular: "商机",
    noteSingular: "笔记",
    nodeFallback: "节点"
  },
  nav: {
    overview: "总览",
    graph: "图谱",
    people: "人物",
    companies: "公司",
    events: "事件",
    schedule: "日程",
    signals: "商业",
    current: "当前",
    privateAgent: "助理",
    workspace: "工作台",
    connectors: "连接器"
  },
  login: {
    tagline: "每一次对话，都沉淀成一张活的关系图谱。",
    headingIn: "欢迎回来",
    headingOut: "登录 Salesko",
    subIn: "你的会话已就绪。",
    subOut: "使用你的 Salesko 账户进入工作台。",
    subOutUnavailable: "Salesko 账户登录尚未配置。",
    continueWith: (provider) => `使用 ${provider} 继续`,
    openWithout: "不登录直接进入",
    notConfigured: "登录方式尚未配置。",
    fineLead: "继续即表示你同意我们的",
    terms: "服务条款",
    privacy: "隐私政策",
    and: "和",
    period: "。"
  },
  workspace: {
    contactsIntelligenceWorkspaceAriaLabel: "Salesko 联系人智能工作台",
    headerAriaLabel: "工作台页头",
    searchPlaceholder: "搜索人物、公司、事件…",
    searchAriaLabel: "搜索图谱",
    searchResultsLabel: "图谱搜索结果",
    tenantControlsAriaLabel: "租户控制",
    opportunitySignalsAriaLabel: "商机信号",
    canonicalRegistryAriaLabel: "标准实体登记",
    selectedEntityAriaLabel: "已选实体",
    detailPanelToggleAriaLabel: "切换详情面板",
    deleteConversationConfirm: "删除此对话？此操作无法撤销。",
    currentCount: (count) => `当前 ${count} 条`,
    savedCount: (count) => `已存 ${count} 条`,
    searchingSaved: "搜索已存",
    noCurrentMatch: "当前画面没有匹配的节点。",
    savedFrames: "已存画面",
    canonicalMatches: "标准匹配结果",
    unavailable: "无法使用",
    searchingSavedFrames: "正在搜索已存画面…",
    noSavedMatch: "已存画面没有匹配的节点。",
    viewsLabel: "视图",
    accountMenuLabel: "账户菜单",
    noProviderConfigured: "尚未配置登录方式",
    quotaUnlimited: "无限",
    quotaUnlimitedTooltip: "你的方案可无限使用 Agent 消息",
    quotaRemaining: (remaining, limit) =>
      `今日 Agent 消息剩 ${remaining} / ${limit} 条 · 00:00 UTC 重置`
  },
  accountSurface: {
    ariaLabel: "账户",
    syncCopy: "同步画面、信号和租户访问。",
    tenantLabel: (tenantId) => `租户 ${tenantId}`,
    noProviderConfigured: "尚未配置登录方式。",
    sessionGrantReady: "Session grant 已就绪"
  },
  authProviders: {
    signInOptions: "登录选项"
  },
  workspaceError: {
    eyebrow: "工作台错误",
    title: "Salesko 图谱工作台渲染失败。",
    fallback: "工作台遇到了未预期的渲染错误。",
    retry: "重试工作台"
  },
  graphStage: {
    graphFieldAriaLabel: "图谱区域",
    relationshipGraphLabel: "关系图谱",
    viewModeLabel: "图谱视图模式",
    show2dTitle: "显示 Obsidian 风格的 2D 力导向图",
    timelineEnabledTitle: "按时间排列事件",
    timelineDisabledTitle: "至少需要 2 个事件才能生成时间线",
    createShareAriaLabel: "创建只读分享链接",
    sharedReadOnlyTitle: "共享视图为只读",
    copyShareTitle: "复制只读分享链接",
    openSettingsAriaLabel: "打开工作台设置",
    openSettingsTitle: "打开设置",
    openGuideAriaLabel: "打开工作台指南",
    openGuideTitle: "打开工作台指南",
    toggleFullscreenAriaLabel: "切换图谱全屏",
    toggleFullscreenTitle: "切换全屏",
    loadingGraphField: "正在加载图谱区域…",
    agentFrameWaiting: "正在等待 Agent 画面。",
    graphNodesAriaLabel: "图谱节点",
    nodeEdgeCount: (nodeCount, edgeCount) => `${nodeCount} 个节点、${edgeCount} 条关系。`,
    projectedGraphCount: (visibleNodeCount, sourceNodeCount, visibleEdgeCount, sourceEdgeCount) =>
      `当前显示 ${visibleNodeCount}/${sourceNodeCount} 个节点、${visibleEdgeCount}/${sourceEdgeCount} 条关系。`,
    layoutDegraded: "后台布局优化不可用，当前显示即时布局。",
    selectNode: (label) => `选择 ${label}`,
    selectRelationship: (label) => `选择关系 ${label}`,
    flatGraphAriaLabel: "2D 力导向图",
    timelineGraphAriaLabel: "时间线关系图",
    timelineLabel: "时间线",
    timelineMomentsAriaLabel: "时间线事件",
    previousTimelineMoment: "上一个事件",
    nextTimelineMoment: "下一个事件",
    timelinePosition: (position, total) => `第 ${position}/${total} 个事件`,
    dateUnknown: "日期未知",
    noLinkedEntities: "暂无关联实体",
    linkedEntityCount: (count) => `${count} 个关联实体`,
    timelineNeedsTwoEvents: "时间线至少需要两个事件。",
    addDatedInteraction: "添加有日期的互动",
    setOpeningMomentTitle: "将此事件保存为画面的打开时刻",
    setOpeningMoment: "设为打开时刻",
    graphControlsAriaLabel: "图谱视图控制",
    recenter: "重新居中",
    zoomIn: "放大",
    zoomOut: "缩小",
    navigatorAriaLabel: "图谱导航器。拖动以旋转图谱。",
    warningsCount: (count) => ` ${count} 条警告。`
  },
  frameIo: {
    dismissStatus: "关闭画面导入/导出/分享状态",
    exporting: (format) => `正在导出 ${format} artifact。`,
    importing: (fileName, format) => `正在将 ${fileName} 导入为新的 ${format} 画面。`,
    importingSimple: (fileName) => `正在导入 ${fileName}。`,
    creatingShareLink: "正在创建只读分享链接。",
    creatingShareLinkShort: "正在创建分享链接。",
    downloaded: (format) => `已下载 ${format} artifact`,
    downloadedFile: (filename) => `已下载 ${filename}。`,
    copiedShareLink: "已复制只读分享链接",
    shareLinkReadyNoCopy: "只读分享链接已就绪；浏览器无法复制",
    shareLinkReady: "分享链接已就绪。",
    shareExpires: (date) => `到期日 ${date}。`,
    importedSummary: (fileName, nodeCount, edgeCount) =>
      `已导入 ${fileName}：${nodeCount} 个节点、${edgeCount} 条关系。`,
    importedOpening: (fileName) => `已导入 ${fileName}。正在打开画面。`,
    fileFallback: "文件",
    obsidianFilesLabel: (count) => `${count} 个 Obsidian 文件`,
    errors: {
      exportFailed: "画面导出失败。",
      shareLinkFailed: "无法创建分享链接。",
      importFailed: "画面导入失败。",
      exportNeedsCommittedFrame: "请先生成并提交图谱画面，再导出。",
      shareNeedsCommittedFrame: "请先生成并提交图谱画面，再分享。",
      jsonImportFailed: "JSON 导入失败。",
      obsidianImportFailed: "Obsidian 导入失败。"
    },
    fileErrors: {
      jsonTooLarge: "JSON 图谱导入上限为 5 MB。",
      jsonInvalid: (fileName) => `${fileName} 不是有效 JSON。`,
      jsonInvalidShape: "JSON 导入文件必须是 Salesko 图谱导出或关系图谱文件。",
      obsidianEmptySelection: "请至少选择一个 Obsidian Markdown 文件或导出 artifact。",
      obsidianJsonTooLarge: "Obsidian JSON artifact 导入上限为 5 MB。",
      obsidianJsonInvalidShape: "Obsidian JSON 导入文件必须是 Salesko Obsidian 图谱导出。",
      obsidianMarkdownTooLarge: (fileName) => `${fileName} 超过 100 KB Obsidian 文件上限。`
    }
  },
  changeSet: {
    dismissStatus: "关闭变更集状态",
    importingFile: (fileName) => `正在将 ${fileName} 导入为待处理变更集。`,
    creatingFor: (sourceLabel) => `正在为 ${sourceLabel} 创建待处理变更集。`,
    pendingSummary: (changeSetId, sourceLabel, commandCount, nodeCount, edgeCount) =>
      nodeCount === undefined || edgeCount === undefined
        ? `待处理变更集 ${changeSetId}（${sourceLabel}）：${commandCount} 个 commands。`
        : `待处理变更集 ${changeSetId}（${sourceLabel}）：${commandCount} 个 commands、${nodeCount} 个节点、${edgeCount} 条关系。`,
    applying: (changeSetId, sourceLabel) => `正在应用变更集 ${changeSetId}（${sourceLabel}）。`,
    reverting: (changeSetId, sourceLabel) => `正在还原变更集 ${changeSetId}（${sourceLabel}）。`,
    applied: (changeSetId, projectionStatus) => `已应用变更集 ${changeSetId}；projection ${projectionStatus}。`,
    reverted: (changeSetId, projectionStatus) => `已还原变更集 ${changeSetId}；projection ${projectionStatus}。`,
    apply: "应用",
    revert: "还原",
    pendingPanel: (changeSetId, sourceLabel) => `待处理变更集 ${changeSetId} 来自 ${sourceLabel}。`,
    updatingSource: (action, sourceLabel) => `${action} ${sourceLabel}。`,
    sourceUpdated: (sourceLabel) => `${sourceLabel} 已更新。正在打开画面。`,
    errors: {
      historyFailed: "变更集历史加载失败。",
      auditLogFailed: "变更集 audit log 加载失败。",
      auditExportFailed: "Audit 导出失败。",
      csvImportFailed: "CSV 导入失败。",
      createFailed: "创建变更集失败。",
      applyFailed: "应用变更集失败。",
      revertFailed: "还原变更集失败。",
      csvNeedsCommittedFrame: "请先生成并提交图谱画面，再导入 CSV。",
      createNeedsCommittedFrame: "请先生成并提交图谱画面，再创建图谱变更。",
      csvTooLarge: "此 API slice 的 CSV 导入上限为 100 KB。",
      csvEmpty: "CSV 文件为空。"
    },
    confirm: {
      csvImport: (fileName) => `将 ${fileName} 导入为当前图谱画面的待处理变更集？`,
      create: (sourceLabel) => `为 ${sourceLabel} 创建待处理变更集？`,
      apply: (summary) => `将 ${summary} 应用到此图谱画面？`,
      applyImportSummary: (commandCount, sourceLabel) => `${commandCount} 个 commands，来源 ${sourceLabel}`,
      applyChangeSetSummary: (changeSetId) => `变更集 ${changeSetId}`,
      revert: (changeSetId, sourceLabel) => `还原 ${sourceLabel} 的变更集 ${changeSetId}？`
    },
    sources: {
      nodeEdit: (label) => `节点编辑：${label}`,
      relationship: (sourceLabel, targetLabel) => `关系：${sourceLabel} -> ${targetLabel}`,
      relationshipEdit: (label) => `关系编辑：${label}`,
      relationshipDelete: (label) => `关系删除：${label}`,
      relationshipTouchpoint: (label) => `关系 touchpoint：${label}`,
      timelineReplay: (label) => `时间线 replay：${label}`,
      leadStageMoveIn: (label) => `移入当前：${label}`,
      leadStageMoveOut: (label) => `移出当前：${label}`,
      targetFallback: "目标",
      currentMoment: "当前时刻"
    }
  },
  leftRail: {
    entitiesLabel: "实体",
    toggleEntity: (label) => `切换 ${label}`,
    relationshipStrengthLabel: "画面关系强度",
    strengthWeak: "弱",
    strengthMedium: "中",
    strengthStrong: "强",
    recentFrames: "最近画面",
    loadingFrames: "正在加载已提交画面…",
    frameHistoryUnavailable: "画面历史目前无法使用。",
    noFramesYet: "已提交的画面会显示在这里。",
    frameNodeCount: (count) => `${count} 个节点`,
    frameLoadError: (frameId) => `无法加载画面 ${frameId}。`,
    recentConversations: "最近对话",
    newConversationAriaLabel: "开始新对话",
    newConversationTitle: "新对话",
    loadingConversations: "正在加载对话…",
    conversationHistoryUnavailable: "对话历史目前无法使用。",
    noConversationsYet: "对话会显示在这里。",
    threadMessageCount: (count) => `${count} 条消息`,
    deleteConversationTitle: "删除对话",
    deleteConversationAriaLabel: (title) => `删除对话 ${title}`,
    conversationLoadError: (threadId) => `无法加载对话 ${threadId}。`
  },
  onboarding: {
    eyebrowGuide: "工作台指南",
    heading: "从一张图谱画面开始",
    graphSummary: (nodeCount, edgeCount) => `当前查看 ${nodeCount} 个节点 · ${edgeCount} 个关系`,
    firstGraphSectionLabel: "第一张图谱流程",
    eyebrowFirstGraph: "第一张图谱",
    captureContextTitle: "捕获上下文",
    captureContextBody: "将会议记录、通话记录或商机上下文粘贴进 Agent 输入框。",
    reviewGraphTitle: "查看图谱",
    reviewGraphBody: "查看候选项、编辑节点或关系，再应用待处理的更改。",
    keepFrameTitle: "保留画面",
    keepFrameBody: "登录后的工作台可从最近画面恢复已提交的画面。",
    openAgent: "打开 Agent",
    startInGraph: "直接进入图谱",
    workspaceStatusLabel: "工作台状态",
    signedInStatus: "已登录",
    signInToPersistStatus: "登录以保留画面",
    frameHistoryActiveStatus: "画面历史已启用",
    noCommittedFrameStatus: "尚未提交任何画面",
    readOnlySharedViewStatus: "只读共享视图",
    workspaceEditableStatus: "工作台可编辑",
    signInOptionsLabel: "工作台登录选项"
  },
  agentChat: {
    graphChat: "Graph Chat",
    openAgentInputAriaLabel: "打开 Agent 输入框",
    agentInputAriaLabel: "Agent 输入",
    headline: "把上下文变成图谱",
    subtitle: "免费 · 即时 · 使用你提供的上下文",
    signedInTitle: "已登录",
    signInOptionsAriaLabel: "登录选项",
    collapseAgentInputAriaLabel: "收起 Agent 输入框",
    collapseTitle: "收起",
    agentConversationAriaLabel: "Agent 对话",
    exampleNoteAriaLabel: "示例说明",
    exampleOnly: "仅供示例",
    useExample: "用这个示例",
    seeExample: "看个示例",
    messageLabel: "消息",
    messagePlaceholder: "粘贴笔记，或请 Graph Chat 更新图谱，也可输入 @ 提及实体…",
    mentionListAriaLabel: "提及图谱实体",
    mentionWithText: (text) => `提及"${text}"`,
    mentionCurrentEntity: "提及当前图谱实体",
    noMentionMatches: "当前画面没有匹配的实体。",
    composerHint: "人物、事件、公司、商机",
    generating: "生成中…",
    send: "发送",
    structuringGraphFrame: "正在结构化图谱画面",
    requestFailed: "请求失败",
    needsReview: "待审核",
    executed: "已执行",
    nodesLabel: "节点",
    edgesLabel: "关系",
    peopleLabel: "人物",
    eventsLabel: "事件",
    sourceModel: "模型",
    sourceRules: "规则",
    sourceAgent: "Agent",
    genericAgentName: "Salesko Agent",
    genericAgentSubtitle: "跑在 Salesko 上",
    handoff: (agentName) => `移交给 ${agentName}`,
    streamStructuring: "正在用 Graph Chat 结构化你的关系上下文。",
    streamAuthRequired: "此笔记保存前，Graph Chat 需要模型抽取。",
    streamRebuilding: "正在读取上下文并重建联系人图谱。",
    streamSeekerStarting: "正在只读查看你的图谱，准备和你聊聊。",
    previewStreamFailed: "Agent 预览流失败。",
    noteIntakeExtractionFailure: "Agent LLM 抽取未生成 graph frame；笔记未持久化。",
    noteIntakeExtractionFailureReason: (reasons) => `原因：${reasons}`,
    noteIntakeCapturing: "正在通过 intake pipeline 捕获笔记。",
    modeToggleAriaLabel: "对话模式",
    modeWriterLabel: "写作",
    modeSeekerLabel: "探讨",
    modeWriterHint: "写作模式：这段对话会更新你的图谱。",
    modeSeekerHint: "探讨模式：只读讨论，不会修改图谱。",
    seekerMessagePlaceholder: "让探讨模式陪你聊聊、分析这张关系图谱——只读，不会做任何修改…"
  },
  detail: {
    emptyTitle: "尚未选择联系人或实体",
    selfMarkedTitle: "此节点已标记为你；点击可清除",
    selfMarkTitle: "将此节点标记为你（图谱的 ego-center）",
    selfYou: "你",
    selfSet: "设为你",
    leadStageInTitle: "将此节点标记为当前跟进对象",
    leadStageOutTitle: "此节点已在当前中；点击可移出",
    leadStageInLabel: "移入当前",
    leadStageOutLabel: "已在当前",
    dismissCalendarEvent: "忽略此事件",
    dismissCalendarEventPending: "正在忽略…",
    dismissCalendarEventDone: "已从图谱中忽略此事件。",
    dismissCalendarEventError: "无法忽略此日历事件，请重试。",
    about: "关于",
    followUp: "跟进",
    contact: "联系方式",
    relationshipContext: "关系背景",
    personalDetails: "个人信息",
    relationshipStageField: "关系阶段",
    relationshipSourceField: "关系来源",
    contactActionsAriaLabel: "联系操作",
    emailAction: "发邮件",
    callAction: "打电话",
    staleByDays: (days) => `已逾期 ${days} 天`,
    noAboutYet: "还没有简介。",
    source: "来源",
    editNodeTitle: "编辑选中节点",
    commitBeforeEditNodesTitle: "编辑节点前请先提交画面",
    editNodeAriaLabel: "编辑选中节点",
    labelField: "标签",
    summaryField: "摘要",
    evidenceField: "证据",
    roleField: "角色",
    companyField: "公司",
    stageField: "阶段",
    sourceField: "来源",
    timeTextField: "时间文本",
    genderField: "性别",
    birthdayField: "生日",
    interestsField: "兴趣喜好",
    emailField: "邮箱",
    phoneField: "电话",
    locationField: "所在地",
    industryField: "行业",
    sizeField: "公司规模",
    revenueField: "年营收",
    websiteField: "官网",
    fieldOptionUnset: "未填写",
    genderOptions: {
      male: "男",
      female: "女",
      other: "其他",
      undisclosed: "不愿透露"
    },
    industryOptions: {
      technology: "科技",
      finance: "金融",
      healthcare: "医疗健康",
      manufacturing: "制造业",
      retail: "零售",
      education: "教育",
      media: "媒体",
      realEstate: "房地产",
      energy: "能源",
      consulting: "咨询",
      government: "政府 / 公共",
      other: "其他"
    },
    saveAsChangeSet: "保存为变更集",
    canonicalIdentityAriaLabel: "标准身份",
    identity: "身份",
    editIdentityTitle: "编辑标准身份",
    commitBeforeEditIdentityTitle: "编辑身份前请先提交画面",
    editIdentityAriaLabel: "编辑标准身份",
    identityActionRunning: (action, sourceLabel) => `${action} ${sourceLabel}`,
    identityActionSaved: (action) => `${action} 已保存`,
    splitLabel: "拆分标签",
    splitSelected: "拆分选中项",
    rekeyLabel: "重设键标签",
    rekeyIdentity: "重设身份键",
    mergeTarget: "合并目标",
    noSameTypeTarget: "没有同类型目标",
    signalsPrefix: (signals) => `信号：${signals}`,
    mergeIdentity: "合并身份",
    reviewCandidateAriaLabel: "审核候选项",
    review: "审核",
    reviewCount: (count) => (count === 1 ? "1 个候选项需要审核" : `${count} 个候选项需要审核`),
    extractionConfidence: "抽取置信度",
    reviewReasonAgentFlagged: "Agent 已标记",
    reviewReasonLowConfidence: (percent) => `${percent}% 置信度`,
    accept: "接受",
    discard: "丢弃",
    linkedEntities: "已连接实体",
    viewAllLinkedTitle: (label) => `在标准登记中查看所有 ${label}`,
    viewAllLinkedAriaLabel: (count, label) => `在标准登记中查看 ${count} 个 ${label}`,
    viewAllLinked: (count) => `查看全部 ${count}`,
    createRelationshipTitle: "创建关系",
    commitBeforeCreateRelationshipTitle: "请先提交至少包含两个节点的画面",
    createRelationshipAriaLabel: "创建关系",
    targetField: "目标",
    typeField: "类型",
    relationshipTypeRelatedTo: "相关于",
    relationshipTypeMetAt: "见于",
    relationshipTypeWorksAt: "任职于",
    relationshipTypeAttended: "参与",
    relationshipTypeIntroducedBy: "由其介绍",
    relationshipTypeOpportunityAt: "商机所在",
    relationshipTypeMentioned: "提及",
    strengthField: (percent) => `强度 ${percent}%`,
    saveRelationship: "保存关系",
    editingRelationship: (endpoint, edgeId) => `正在编辑 ${endpoint} · ${edgeId}`,
    saveRelationshipChanges: "保存关系变更",
    existingRelationshipsAriaLabel: "已有关系",
    editRelationshipAriaLabel: (label) => `编辑关系 ${label}`,
    deleteRelationshipAriaLabel: (label) => `删除关系 ${label}`,
    editRelationshipTitle: "编辑关系",
    commitBeforeEditRelationshipsTitle: "编辑关系前请先提交画面",
    deleteRelationshipTitle: "删除关系",
    commitBeforeDeleteRelationshipsTitle: "删除关系前请先提交画面",
    linkedGroupsAriaLabel: "已连接实体分组",
    noLinkedYet: (label) => `尚未连接 ${label}。`,
    frameCommit: "画面提交",
    sharedStatus: "已共享",
    previewStatus: "预览",
    idleStatus: "空闲",
    readOnlyGraphFrame: "只读图谱画面",
    defaultGraphFrame: "默认图谱画面",
    waitingForGraphFrame: "等待图谱画面",
    advanced: "高级",
    relationshipDirectionOut: "由此实体发出",
    relationshipDirectionIn: "指向此实体",
    identityActionMerge: "合并",
    identityActionRekey: "重设键",
    identityActionSplit: "拆分"
  },
  relationshipIntel: {
    ariaLabel: "关系智能",
    title: "关系智能",
    signInCommittedFrame: "登录并打开已提交画面后，才能加载关系智能。",
    addAnotherEntity: "新增另一个实体以检查介绍路径。",
    targetLabel: "介绍路径目标",
    pathsTo: (label) => `通往 ${label} 的路径`,
    introductionPaths: "介绍路径",
    loadingPaths: "正在加载介绍路径",
    pathsUnavailable: "关系路径目前不可用。",
    noPath: "这组对象没有找到介绍路径。",
    commonNeighbor: "共同邻居",
    direct: "直接",
    seenInFrames: (count) => `出现在 ${count} 个画面`,
    staleReminders: "冷却关系提醒",
    loadingStale: "正在加载冷却关系",
    remindersUnavailable: "关系提醒目前不可用。",
    noStaleForEntity: (count) => `此实体没有冷却关系；此租户其他位置有 ${count} 个。`,
    noStaleOlderThan: (days) => `没有超过 ${days} 天的冷却关系。`,
    lastSeenStaleBy: (lastSeen, days) => `最后出现 ${lastSeen} · 冷却 ${days} 天`,
    recordTouchpointTitle: "将 touchpoint 记录为待处理变更集",
    openLastSeenFrameTitle: "打开此关系最后出现的画面",
    logTouchpoint: "记录 touchpoint",
    openInFrame: "在画面中打开"
  },
  changeSetHistory: {
    ariaLabel: "变更集历史",
    title: "变更集",
    refreshAriaLabel: "刷新变更集历史",
    refreshTitle: "刷新变更集历史",
    noCommit: "生成并提交画面后才能查看 mutation 历史。",
    loading: "正在加载变更集历史",
    empty: "此画面尚无变更集记录。",
    downloaded: (filename) => `已下载 ${filename}`,
    auditLog: "Audit log",
    auditLogFor: (changeSetId) => `${changeSetId} 的 audit log`,
    loadingAuditLog: "正在加载 audit log",
    emptyAuditLog: "此变更集没有 audit log 事件。",
    graphMutation: "图谱 mutation",
    commandCount: (count) => `${count} 个 commands`,
    exportAuditAriaLabel: (changeSetId) => `导出 ${changeSetId} 的 audit artifact`,
    inspectAuditAriaLabel: (changeSetId) => `检查 ${changeSetId} 的 audit log`,
    exportAuditTitle: "导出 audit JSON",
    auditLogTitle: "Audit log",
    columnChangeSet: "变更集",
    columnStatus: "状态",
    columnCommands: "Commands",
    columnUpdated: "更新时间",
    columnActions: "操作",
    columnEvent: "事件",
    columnCreated: "创建时间",
    columnDetails: "详情",
    auditCreated: "已创建",
    auditApplied: "已应用",
    auditReverted: "已还原",
    privateAgentSource: "Private Agent"
  },
  signals: {
    title: "商机收件箱",
    eyebrow: "跨全部已保存图谱记录",
    inboxSummary: (pending, saved) => `当前显示：${pending} 个待处理 · ${saved} 个已保存`,
    unavailable: "商机收件箱不可用",
    viewModeAriaLabel: "商机收件箱视图模式",
    cardViewTitle: "卡片视图",
    tableViewTitle: "表格视图",
    cards: "卡片",
    table: "表格",
    signInToLoad: "登录以加载你的私人商机收件箱。",
    loading: "正在加载商机收件箱…",
    empty: "当前没有待处理或已保存的商机。",
    actionsFor: (title) => `${title} 的操作`,
    openSourceFor: (title) => `打开 ${title} 的来源画面`,
    openSourceFrame: "打开来源画面",
    updated: "更新时间",
    evidence: "证据",
    source: "来源",
    confidence: (score) => `信心分数 ${score}%`,
    whySurfaced: "出现原因",
    followUpRequested: (date) => `已加入未排程跟进 ${date}`,
    details: "详情",
    reasonUnavailable: "此商机没有记录出现原因。",
    evidenceAvailable: "可查看",
    evidenceUnavailable: "未提供",
    evidenceSpanCount: (count) => `${count} 个 evidence spans`,
    relatedEntities: "相关实体",
    relatedFrame: "相关画面",
    sourceAvailable: "可打开来源画面",
    sourceUnavailable: "未提供",
    suggestedNextAction: "建议下一步",
    status: {
      pending: "待处理",
      saved: "已保存",
      dismissed: "已忽略",
      archived: "已归档"
    },
    actions: {
      dismiss: "忽略",
      save: "保存商机",
      saved: "已保存",
      followUp: "加入未排程跟进",
      followUpAdded: "已加入跟进"
    },
    columns: {
      actions: "操作",
      evidence: "证据",
      score: "信心分数",
      source: "来源",
      status: "状态",
      title: "标题",
      updatedAt: "更新时间"
    }
  },
  activityFeed: {
    surfaceAriaLabel: "商业动态与商机",
    closeSurface: "关闭商业面板",
    tabsAriaLabel: "商业功能",
    activityTab: "活动记录",
    inboxTab: "商机收件箱",
    title: "活动记录",
    eyebrow: "已记录的事实与变更",
    pageSummary: (count, hasNext) => hasNext ? `显示最近 ${count} 条记录` : `共 ${count} 条记录`,
    unavailable: "活动记录不可用",
    loading: "正在加载活动记录…",
    empty: "暂时没有已记录的活动。",
    recordedAt: "记录时间",
    occurredAt: "发生时间",
    timeUnavailable: "未记录",
    backfilled: "历史补录",
    backfilledLabel: "是否补录",
    backfilledValue: (backfilled) => backfilled ? "是" : "否",
    correction: "更正",
    supersedes: "取代一条较早记录",
    evidence: "证据",
    evidenceStates: {
      available: "可查看",
      sourceUnavailable: "来源不可用",
      redacted: "已遮盖"
    },
    entryTypes: {
      eventOccurred: "事件发生",
      opportunitySurfaced: "商机出现",
      transactionRecorded: "交易已记录",
      transactionStatusChanged: "交易状态已变更",
      transactionCorrected: "交易已更正"
    },
    subjectTypes: {
      event: "事件",
      opportunity: "商机",
      transaction: "交易"
    },
    inspectSource: (index) => `查看来源 ${index}`,
    inspectSourceFor: (title, index) => `查看 ${title} 的来源 ${index}`,
    inspectTransaction: "查看交易",
    inspectTransactionFor: (title) => `查看 ${title} 的交易详情`,
    openTransactionGraph: "在图谱中打开",
    openTransactionGraphFor: (title) => `在图谱中打开：${title}`,
    loadOlder: "加载较早记录",
    loadingOlder: "正在加载较早记录…",
    sourceDetailTitle: "精确来源",
    transactionDetailTitle: "当前交易",
    transactionGraphTitle: "交易图谱",
    transactionGraphReadOnly: "当前事实及其有证据支持的连接 · 只读",
    closeSourceDetail: "关闭来源详情",
    closeTransactionDetail: "关闭交易详情",
    closeTransactionGraph: "关闭交易图谱",
    loadingSource: "正在加载精确来源…",
    loadingTransaction: "正在加载当前交易…",
    loadingTransactionGraph: "正在加载交易图谱…",
    transactionGraphUnavailable: "交易图谱暂时不可用。",
    selectTransactionGraphNode: "选择一个节点以查看其证据。",
    exactQuote: "原文引句",
    evidenceSpan: "证据区段",
    sourceFrame: "来源画面",
    sourceNode: "来源节点",
    openSourceFrame: "打开来源画面",
    kind: "类型",
    externalId: "外部 ID",
    status: "状态",
    amount: "金额",
    parties: "相关方",
    provenance: "来源记录",
    acceptedAt: "接受时间",
    evidenceText: "证据",
    authority: "权威来源",
    sourceFact: "来源事实",
    sourceFrames: "来源画面",
    evidenceSpans: "证据区段",
    sourceReferences: "来源引用",
    transactionKinds: {
      contract: "合约",
      order: "订单",
      invoice: "发票",
      payment: "付款"
    },
    transactionStatuses: {
      committed: "已确认",
      settled: "已结清",
      cancelled: "已取消"
    }
  },
  settings: {
    navigationAriaLabel: "设置导航",
    sectionsAriaLabel: "设置区段",
    title: "设置",
    workspaceSettings: "工作台设置",
    description: "集中管理你的账户、Agent 默认值和数据。",
    signOut: "退出登录",
    billingSuccess: "方案已启用。Deep Research 现已可用；Agent credits 已加入。",
    billingCancel: "Checkout 已取消。你的方案未变化。",
    sidebar: {
      account: "账户",
      accountDescription: "命名你的工作台",
      billing: "Billing",
      billingDescription: "Messaging addon 方案",
      credits: "Credits",
      creditsDescription: "Agent credit 余额",
      computers: "电脑",
      computersDescription: "连接运行 agent 的那台电脑",
      privateAgent: "Private Agent",
      privateAgentDescription: "命名、绑定电脑、选择 runtime",
      agent: "Agent",
      agentDescription: "语言、区域、渠道",
      data: "数据",
      dataDescription: "导出工作台数据",
      advanced: "高级",
      advancedDescription: "文件导入与画面 artifacts"
    }
  },
  billing: {
    ariaLabel: "Billing",
    title: "Messaging channels",
    signIn: "登录以管理 messaging addon。",
    active: "Messaging addon 已启用",
    pendingCancel: "将在周期结束时取消",
    renews: (date) => `续订日 ${date}`,
    upsell: "Telegram、WeChat 和 Line delivery 已包含在任何付费方案中。",
    viewPlans: "查看方案"
  },
  credits: {
    ariaLabel: "Agent credits",
    title: "Agent credits",
    signIn: "登录以查看 Agent credits。",
    upgrade: "升级付费方案以解锁 Deep Research。",
    byok: "Deep Research 在你自己的电脑上运行，用你自己的 provider 登录态或 key；不消耗 Salesko credits。",
    available: "可用 credits",
    empty: "暂无 credit activity。",
    loadError: "无法加载 credit 余额。请稍后再试。"
  },
  computers: {
    ariaLabel: "电脑",
    title: "电脑",
    description: "Private Agent 跑在你在这里连接的电脑上。Salesko 负责协调，运算在你的电脑。",
    signIn: "登录以连接电脑。",
    connect: "连接一台电脑",
    stepInstallTitle: "1 · 安装并配对",
    stepInstallBody: "先创建一次性配对码，再到目标 Mac 粘贴生成的连接命令。它会一次完成安装、配对和后台启动。",
    stepInstallPrereq: "仅支持 macOS（Apple 芯片或 Intel）。安装脚本会挑选对应这台 Mac 的构建，校验官方发布的 checksum 之后再安装。",
    stepInstallGuideLink: "打开安装指南",
    setupExisting: "已经安装？这条 setup 命令会完成配对并配置后台启动。",
    updateTitle: "更新 Agent · 保留配对",
    updateBody: "在已配对电脑的终端执行，安装最新版本并重启后台服务。保留现有配对，无需生成新的配对码。",
    startupTitle: "启动配置",
    startupBody: "在已经配对的电脑上执行这条命令，可修复或刷新后台服务。",
    createPairingCode: "创建配对码",
    copy: "复制",
    copied: "已复制",
    pairingCodeExpiresAt: (expiresAt) => `配对码 ${expiresAt} 到期`,
    stepWaitingTitle: "2 · 等待电脑上线",
    stepWaitingBody: "执行上面那条命令。电脑一上报就会出现在这里。",
    emptyTitle: "还没有连接电脑",
    empty: "创建配对码，连接 Private Agent 要运行的那台电脑。",
    presenceIsHint: "Presence 是短时 TTL hint，不代表 readiness 或已执行。",
    presenceHint: (level, expiresAt) => `${level} hint，至 ${expiresAt}`,
    noPresenceHint: "暂无 presence hint",
    online: "在线",
    offline: "离线",
    installedRelease: (version) => `本机版本：${version}`,
    installedReleaseUnavailable: "本机版本：未上报",
    latestRelease: (version) => `最新版本：${version}`,
    latestReleaseLoading: "最新版本：加载中",
    latestReleaseUnavailable: "最新版本：暂不可用",
    runtimes: "检测到的 runtime",
    noRuntimeFacts: "这台电脑还没上报它有哪些 runtime。",
    signedIn: "已登录",
    notSignedIn: "未登录",
    revoked: "已撤销",
    revoke: "撤销",
    keysStayOnComputer: "Provider 登录态和 API key 都留在电脑上。在那台电脑执行：",
    updateError: "无法更新你的电脑列表。请稍后再试。"
  },
  privateAgent: {
    ariaLabel: "Private Agent",
    title: "Private Agent",
    description: "你自己的 agent，绑定一台电脑，用你自己的 runtime 登录态。Salesko 看不到这些凭据。",
    signIn: "登录以设置 Private Agent。",
    loading: "正在加载 Private Agent",
    name: "Agent 名称",
    namePlaceholder: "给你的 agent 起个名字",
    computer: "电脑",
    computerHint: "Agent 只跑在一台已连接的电脑上。",
    runtime: "Runtime",
    runtimeHint: "不管选哪个，登录态都留在你的电脑上。",
    piVendor: "厂商预设",
    piVendorHint: "选择厂商后会列出该厂商的 model，并生成本机配置命令。下方配置 ID 必须与电脑上的配置一致。",
    piVendorPlaceholder: "选择厂商",
    piConfigureHint: "在 Agent 电脑上执行。API key 在终端隐藏输入并保存在本机；此命令将该配置设为设备默认配置。",
    subscriptionModel: "模型",
    runtimeDefaultModel: "使用 runtime 默认模型",
    specifyModel: "指定模型",
    modelId: "模型代号或 ID",
    subscriptionModelHint: "输入该 Agent 和账号支持的模型代号或 ID。代号对应的版本可能随时间更新；不可用的模型会在执行时报错。",
    piProvider: "Pi 配置 ID",
    piProviderHint: "填写 salesko-agent keys list 返回的准确 profile_ref，不是厂商名称。",
    piProviderPlaceholder: "openrouter",
    piModel: "Pi model",
    piModelHint: "从所选厂商公布的 model 中选择；请先选择厂商。",
    piModelPlaceholder: "选择 model",
    runtimes: {
      claudeSubscription: "Claude 订阅",
      codexSubscription: "Codex 订阅",
      piProviderKey: "Pi + 你的 provider key"
    },
    researchPreset: "研究 preset",
    presetHint: "这是同一个 research agent 的区域 preset，不是多个 agent。",
    profiles: {
      default: "默认",
      mainland: "中国大陆 (CN)",
      taiwan: "台湾 (TW)",
      hongKong: "香港 (HK)"
    },
    save: "保存 Private Agent",
    saved: "Private Agent 已保存。",
    projectionPending: "正在等待绑定的电脑应用这份 Profile。",
    projectionSynced: "绑定的电脑已应用这份 Profile。",
    projectionFailed: "绑定的电脑无法应用这份 Profile。",
    remove: "删除 agent",
    removeConfirm: "删除这个 Private Agent？在重新创建之前研究任务会停。",
    removeConfirmAction: "删除",
    removeCancel: "先留着",
    deviceNotActive: "那台电脑已经不可用了。请选一台已连接的。",
    needsRebindingTitle: "需要重新绑定",
    needsRebinding: "这个 agent 绑定的电脑已被撤销或不存在。选另一台再保存。",
    noComputersTitle: "还没有连接电脑",
    noComputers: "先连接一台电脑——Private Agent 总得有地方跑。",
    goToComputers: "去连接电脑",
    loadError: "无法加载 Private Agent。请稍后再试。",
    saveError: "无法保存 Private Agent。请稍后再试。"
  },
  privateAgentJobs: {
    ariaLabel: "Private Agent 研究任务",
    eyebrow: "Private Agent",
    title: "Private Agent",
    signIn: "登录后查看 Private Agent 正在做什么。",
    loading: "正在加载 Private Agent",
    loadingJobs: "正在加载研究任务",
    loadError: "无法加载研究任务，请稍后再试。",
    emptyTitle: "还没有研究任务",
    empty: "打开一个人物或商机，让 Private Agent 去研究。",
    noAgentTitle: "还没有 Private Agent",
    noAgent: "先给 agent 起名并绑定一台已连接的电脑，它才能开始研究。",
    setUpAgent: "去设置 Private Agent",
    openComputers: "打开 Computers",
    computerOffline: "运行这个 agent 的电脑当前离线。排队中的任务会在它上线后开始。",
    computerRevoked: "这个 agent 绑定的电脑已被撤销。连接一台电脑并重新绑定。",
    computer: {
      online: "电脑在线",
      offline: "电脑离线",
      revoked: "电脑已撤销",
      missing: "未绑定电脑"
    },
    status: {
      queued: "排队中",
      dispatching: "派发中",
      running: "运行中",
      cancelRequested: "取消中",
      succeeded: "待审阅",
      failed: "失败",
      canceled: "已取消",
      waitingDevice: "等待你的电脑",
      erased: "已清除"
    },
    statusDetail: {
      waitingDevice: "没有已连接的电脑接下这次研究。连接一台后重新发起。",
      queuedComputerOffline: "排队中 \u2014 电脑离线",
      queuedNoComputer: "排队中 \u2014 没有已连接的电脑"
    },
    stopReasons: {
      completed: "已完成",
      stepBudget: "达到步数预算",
      timeBudget: "达到时间预算",
      tokenBudget: "达到 token 预算",
      providerLimit: "被 provider 限额中断"
    },
    stopReason: (label) => `停止原因：${label}`,
    startedAt: (when) => `开始于 ${when}`,
    jobId: (id) => `任务 ${id}`,
    review: "审阅变更",
    reviewPending: "准备中\u2026",
    cancel: "取消",
    canceling: "取消中\u2026",
    showDetails: "详情",
    hideDetails: "收起详情",
    loadingDetails: "正在加载详情",
    noResultDetail: "这个任务没有返回结果。",
    frameConflict: "研究开始后图谱已经变了。请在当前图谱上重新发起。",
    resultNotReady: "这次研究还没有可审阅的结果。",
    materializeError: "无法生成变更集，请稍后再试。",
    changeSetSourceLabel: (agentName) => `${agentName} 的研究`,
    question: (entityLabel) => `研究 ${entityLabel}，并提出关系图谱的更新建议。`,
    trigger: {
      ariaLabel: "Private Agent 研究",
      eyebrow: "Private Agent",
      action: (agentName) => `让 ${agentName} 研究`,
      actionFallback: "让 Private Agent 研究",
      starting: "正在发起\u2026",
      checkingAccess: "正在确认你的套餐\u2026",
      signedOut: "登录后才能在自己的电脑上跑研究。",
      entitlementRequired: "Deep Research 包含在付费套餐里。",
      profileRequired: "先给 Private Agent 起名并绑定一台已连接的电脑。",
      noActiveComputer: "先连接一台电脑给 Private Agent 运行。",
      activeJob: "Private Agent 已经在跑一个研究任务。",
      activeJobConflict: "Private Agent 已经在跑一个研究任务。等它结束，或者先取消。",
      noCommittedFrame: "先提交一份图谱。研究是针对已保存的图谱提变更。",
      viewPlans: "查看套餐",
      openJobs: "打开助理",
      insufficientCredits: "Agent credits 不足，无法发起研究。请充值或升级套餐。",
      frameVersionUnavailable: "这份图谱没有版本号可供研究。刷新后重试。",
      createError: "无法发起研究，请稍后再试。"
    }
  },
  privateAgentWorkspace: {
    recovery: {
      queueLabel: "回合队列",
      replyDelivered: "回复已送达",
      ended: "本轮已结束，无回复",
      withdrawn: "执行前已停止",
      stopPending: "已请求停止，正在核对执行",
      checking: "正在核对执行状态",
      needsAttention: "尚无回复，需要处理",
      notStarted: "本轮未启动",
      messageHeld: "消息被暂存，需处理",
      messageRefused: "消息已被拒绝",
      cleanupUnknown: "执行已结束，清理状态未确认",
      retryTurn: "重试本轮",
      endTurn: "结束本轮",
      stopAndSend: "停止并发送",
      retryPending: "重发同一请求",
      pendingRequest: "响应尚未确认。将使用已保存的身份和目标重发同一请求。",
      queueFull: "队列已满。请先结算一个回合，再发送新输入。",
      actionConflict: "目标已变化。请刷新后重新选择操作。",
      storageFailure: "请求未发送：浏览器存储不可用或记录无效。请刷新核对。",
      queueCount: (count) => `未结算 ${count} / 8 轮`
    },
    ariaLabel: "Private Agent 工作区",
    conversationsAriaLabel: "Private Agent 对话",
    facetsAriaLabel: "Private Agent 工作视图",
    chat: "聊天",
    research: "研究",
    recentConversations: "最近对话",
    noConversations: "还没有对话。",
    loadingConversations: "正在加载对话",
    newConversation: "新建对话",
    creatingConversation: "正在创建…",
    messageCount: (count) => `${count} 条消息`,
    identitySubtitle: "在你的电脑上工作，回答只留在你这里。",
    deviceLine: (deviceName, state) => `在 ${deviceName} 上运行 · ${state}`,
    computerState: {
      online: "在线",
      offline: "不在线",
      revoked: "已移除",
      missing: "未连接"
    },
    selectConversationTitle: "开始或选择一个对话",
    selectConversation: "从左边选一个对话，或者新建一个。",
    setupTitle: "先设置你的助理",
    profileRequired: "先给助理起个名字，并选一台电脑给它用，然后再开始对话。",
    computerOfflineTitle: "你的电脑不在线",
    updateTitle: "电脑上的助理需要更新或登录",
    signInTitle: "登录后使用助理",
    signIn: "登录后查看或开始和助理的对话。",
    openComputers: "查看电脑",
    loadingConversation: "正在加载对话",
    transcriptAriaLabel: "对话记录",
    emptyTranscript: "问点什么吧，比如：帮我整理一下最近和 Acme 的往来。",
    composerLabel: "给助理发消息",
    composerPlaceholder: "给助理发消息…",
    send: "发送",
    sending: "正在发送…",
    checkExecution: "重新检查",
    cancelTurn: "停止",
    cancelingTurn: "正在停止…",
    elapsed: (value) => `已用 ${value}`,
    progressThinking: "正在思考",
    progressUsingTool: "正在使用工具",
    progressWaitingApproval: "等待你确认",
    boundDeviceRevokedNotice: "这个对话用的电脑已经不能用了，请新建对话。",
    readiness: {
      profile_missing: "先给助理起个名字，并选一台电脑给它用，然后再开始聊天。",
      device_missing: "助理原来用的那台电脑已经不在你的列表里了。去「电脑」重新连接，或者选一台已连接的电脑。",
      device_revoked: "助理原来用的那台电脑已被移除。去「电脑」选一台已连接的电脑。",
      device_offline: "助理所在的电脑不在线。让它上线后再试。",
      agent_upgrade_required: (installedRelease) =>
        installedRelease
          ? `电脑上的 Salesko Agent 版本过旧（${installedRelease}），请更新后再试。`
          : "电脑上的 Salesko Agent 版本过旧，请更新后再试。",
      runtime_missing: "电脑上还没安装 Claude 或 Codex。安装并登录后再试。",
      runtime_not_authenticated: "电脑上的 Claude 或 Codex 还没登录。在那台电脑的终端里登录后再试。",
      toolset_not_configured: "电脑上的 Salesko Agent 需要更新并重启。",
      runtime_unsupported_for_tools: "这个对话用的模型工具不支持读取 Salesko 数据。请新建对话。",
      profile_identity_unavailable: "这个对话的助理已经不在了。重新设置同一个助理后再发送。"
    },
    agent: "助理",
    you: "你",
    youInitial: "你",
    turnStatus: {
      blocked: "等待处理",
      queued: "排队中",
      dispatching: "正在联系你的电脑",
      running: "正在处理",
      cancel_requested: "正在停止",
      succeeded: "已完成",
      failed: "失败",
      canceled: "已停止"
    },
    errors: {
      activeConflict: "助理还在处理上一条消息，等它回复后再发。",
      profileRequired: "先把助理设置好，再开始对话。",
      replayGap: "对话记录需要刷新。",
      reconcileFailed: "暂时无法确认助理的状态，请重试。",
      chatPaused: "聊天服务已暂时暂停，请稍后重试。",
      requestFailed: "助理暂时不可用，请稍后再试。"
    },
    researchEmptyTitle: "还没有研究任务",
    researchEmpty: "打开一个人物或商机，让助理去研究。",
    targetUnavailable: "研究目标暂不可用",
    contextAriaLabel: "此对话已载入的人脉、公司与商机",
    touchedEntitiesTitle: "此对话已载入的人脉、公司与商机",
    touchedEntitiesEmpty: "助理回答时查看过的人脉、公司和商机会列在这里。",
    touchedEntitiesError: "这些名称读取失败，对话本身不受影响。",
    touchedEntityUnavailable: "不可用",
    openEntityInGraph: (label) => `在图谱里打开 ${label}`,
    detailTabsAriaLabel: "Private Agent 侧栏视图",
    detailTab: "详情",
    conversationTab: "本次对话"
  },

  dataTools: {
    ariaLabel: "高级数据工具",
    title: "高级数据工具",
    import: "导入",
    export: "导出",
    csvChangeSet: "CSV 变更集",
    jsonFrame: "JSON 画面",
    obsidianFiles: "Obsidian 文件",
    activeFrame: (frameRecordId) => `当前画面 ${frameRecordId}`,
    needsCommittedFrame: "从已提交工作台画面打开设置，才能启用 frame-scoped 工具。"
  },
  tenantAdmin: {
    workspaceAriaLabel: "工作台",
    workspaceTitle: "工作台",
    signInWorkspace: "登录以管理你的工作台。",
    loadingWorkspace: "正在加载工作台",
    workspaceName: "工作台名称",
    saveName: "保存名称",
    workspaceNameSaved: "工作台名称已保存。",
    agentSettingsAriaLabel: "Agent 设置",
    agentDefaults: "Generic Agent 默认值",
    signInAgentDefaults: "登录以管理 Generic Agent 默认值。",
    checkingAccess: "正在检查付费方案，然后解锁 Generic Agent 默认值。",
    defaultsFrozen: "方案可验证前，Generic Agent 默认值保持锁定。",
    defaultsLocked: "启用付费订阅后可解锁 Generic Agent 默认值。",
    loadingDefaults: "正在加载 Generic Agent 默认值",
    language: "语言",
    region: "区域",
    timezone: "时区",
    saveDefaults: "保存 Generic Agent 默认值",
    defaultsSaved: "Generic Agent 默认值已保存。",
    dataExportAriaLabel: "数据导出",
    dataExport: "数据导出",
    dataExportDescription: "下载工作台所有数据的 ZIP：图谱、笔记和 audit trail。",
    exportZip: "导出 ZIP",
    exportStarted: "工作台 ZIP 导出已开始。",
    requestFailed: "工作台请求失败。",
    locales: {
      en: "English",
      "zh-CN": "简体中文",
      "zh-TW": "繁體中文"
    }
  },
  canonicalRegistry: {
    eyebrow: "标准登记",
    allTitle: "标准身份",
    count: (count) => `${count} 个租户身份`,
    signedInRegistry: "登录后的租户登记",
    signIn: "使用租户 session 登录后，可浏览已提交画面中的标准身份。",
    unavailable: "标准登记目前不可用。",
    loading: "正在加载标准身份…",
    noSearchMatch: "当前搜索没有匹配的标准身份。",
    empty: "暂无标准身份。",
    openIdentity: "打开身份",
    openIdentityAriaLabel: (label) => `打开 ${label}`,
    projectedCounts: (nodeCount, frameCount) => `${nodeCount} 个投影节点，跨 ${frameCount} 个画面`,
    columns: {
      actions: "操作",
      confidence: "置信度",
      frames: "画面",
      identity: "身份",
      lastSeen: "最后出现",
      nodes: "节点",
      type: "类型"
    }
  },
  mobile: {
    graph: "图谱",
    inbox: "收件箱",
    you: "你",
    listView: "列表",
    graphCaption: "点选节点；越大的点代表连接越多",
    graphNodeAriaLabel: (label, typeLabel) => `${label}，${typeLabel}`,
    graphSelfNodeAriaLabel: (label) => `${label}，你`,
    primaryNavAriaLabel: "主导航",
    notificationsTitle: "通知",
    captureMeeting: "记录会议",
    selectedEntity: "已选实体",
    close: "关闭",
    clearSearch: "清除搜索",
    relationshipReminder: "关系提醒",
    relationshipReminderBody: (sourceLabel, targetLabel, days) => `${sourceLabel} -> ${targetLabel} 已超过 touchpoint window ${days} 天。`,
    opportunitySignal: "商机",
    contractEmit: "Agent 会生成节点、关系、时间线和视角。",
    generatingGraphFrame: "正在生成图谱画面…",
    generateGraphFrame: "生成图谱画面",
    noDatedEvents: "此画面暂无带日期的事件。",
    undated: "未标日期",
    linkedEntitiesCount: (count) => `已关联实体 · ${count}`,
    thisWeek: "跨全部已保存图谱记录",
    opportunityHero: "处理新商机、保存值得推进的项目，并加入未排程跟进。",
    activeSignalsAcross: (opportunityCount) => `当前显示 ${opportunityCount} 个商机`,
    opportunityFeed: "商机收件箱",
    signInSignalsBody: "登录后，你的私人商机收件箱会显示在这里。",
    emptySignalsBody: "当前没有待处理或已保存的商机。",
    updatedAt: (date) => `更新于 ${date}`,
    evidenceCount: (count) => `${count} 条 evidence`,
    entityCount: (count) => `${count} 个实体`,
    frame: "画面",
    notesCaptured: "你捕获的笔记。Agent 已将其结构化为图谱节点。",
    committedFrames: "已提交画面",
    current: "当前",
    frameNodeSummary: (nodeCount, date) => `${nodeCount} 个节点 · ${date}`,
    noCommittedFrames: "暂无已提交画面",
    signInToSync: "登录以同步租户图谱访问",
    installedOnThisDevice: "已安装在此设备",
    installSalesko: "安装 Salesko",
    installedPwaDescription: "全屏打开 · 可离线使用",
    installReadyDescription: "添加到主屏幕：全屏、可离线",
    installFallbackDescription: "Share -> Add to Home Screen 以获得 app 体验",
    install: "安装",
    mcpAccess: "MCP 访问",
    signInRequired: "需要登录",
    proPlanRequired: "需要 Pro 方案",
    signalsAndReminders: "信号与提醒",
    tenantFeedActive: "租户 feed 已启用",
    inputLanguage: "输入语言",
    privacy: "隐私",
    localPreview: "本地预览",
    graphCachedOffline: "你的图谱已缓存，无连接也可阅读。",
    markAllRead: "全部标为已读",
    refreshingSignals: "正在刷新信号…",
    noNotificationsTitle: "暂无通知",
    notificationsEmpty: "关系提醒和商机信号会显示在这里。",
    browserDefault: "浏览器默认",
    noMatchesFor: (query) => `找不到"${query}"的结果`
  },
  connectors: {
    navigationAriaLabel: "连接器导航",
    categoriesAriaLabel: "连接器分类",
    brandLabel: "连接器",
    eyebrow: "Integrations",
    title: "App Connectors",
    description: "双向进入同一张图谱：向 Agent 发消息以捕获笔记，或让它读取你已使用的账户。",
    searchPlaceholder: "搜索连接器…",
    noMatchesTitle: "没有匹配项",
    noMatchesBody: (categoryLabel) => `没有 ${categoryLabel} 连接器匹配搜索。`,
    tierPro: "Pro",
    comingSoon: "即将推出",
    premium: "Premium",
    upgradeToUnlock: "升级以解锁",
    available: "可用",
    connect: "连接",
    connected: "已连接",
    manage: "管理",
    disconnect: "断开连接",
    connectError: "无法更新 Gmail 连接，请重试。",
    calendarConnectError: "日历连接失败，请重试。",
    connectedAccount: (email) => `已连接：${email}`,
    oneTimeConnected: "Gmail 已连接。",
    oneTimeError: "Gmail 连接失败，请重试。",
    syncNow: "立即同步",
    syncing: "同步中…",
    syncQueued: "同步已加入队列，新的 lead 稍后就会出现。",
    calendarSyncQueued: "日历同步已加入队列。",
    syncCompleted: "同步完成。",
    syncError: "无法启动 Gmail 同步，请重试。",
    calendarSyncError: "无法启动日历同步，请重试。",
    lastSyncedAt: (date) => `上次同步：${date}`,
    lastSyncFailed: (message) => (message ? `上次同步失败：${message}` : "上次同步失败。"),
    reconnectRequired: "Google 授权已被撤销，请重新连接以继续同步 lead。",
    direction: {
      inbound: "Inbound · 笔记捕获",
      outbound: "Outbound · 数据来源"
    },
    lockCopy: {
      signed_out: "登录以解锁 premium connectors。",
      checking: "正在检查 paid Sidecar access 以解锁 premium connectors。",
      verify_failed: "在 Sidecar access 可验证前，premium connectors 会保持锁定。",
      locked: "启用 paid Sidecar subscription 后可解锁 premium connectors。"
    },
    categories: {
      messaging: {
        eyebrow: "笔记捕获",
        label: "Messaging",
        blurb: "直接在这些渠道向你的个人 Agent 发消息；它会在同一串对话回复。"
      },
      email: {
        eyebrow: "Inbox intelligence",
        label: "Email",
        blurb: "让 Agent 读取邮件，将 threads 变成可搜索的关系笔记。"
      },
      contacts: {
        eyebrow: "人物同步",
        label: "Contacts",
        blurb: "自动将你已认识的人带入图谱。"
      },
      calendar: {
        eyebrow: "会议信号",
        label: "Calendar",
        blurb: "同步日历，让每场会议与参会者都成为图谱中的关系信号。"
      }
    },
    items: {
      telegram: {
        displayName: "Telegram",
        tagline: "在 Telegram 向 Agent 发消息即可随手捕获笔记；它会在同一串对话回复。"
      },
      wechat: {
        displayName: "微信 WeChat",
        tagline: "向 Agent 发送微信消息以记录笔记或联系人；它会在原对话回复。"
      },
      line: {
        displayName: "Line",
        tagline: "向 Agent 发送 Line 消息以捕获会议笔记；回复会回到同一个聊天。"
      },
      gmail: {
        displayName: "Gmail",
        tagline: "让 Agent 读取并整理 Gmail，将邮件 threads 变成可搜索的关系笔记。"
      },
      "microsoft-email": {
        displayName: "Microsoft Email",
        tagline: "让 Agent 读取 Outlook 邮件，将对话变成可搜索的关系信号。"
      },
      "google-contacts": {
        displayName: "Google Contacts",
        tagline: "同步 Google Contacts，让已认识的人自动出现在图谱中。"
      },
      "google-calendar": {
        displayName: "Google Calendar",
        tagline: "同步 Google 日历，让会议与参会者作为 event 信号流入图谱。"
      },
      "microsoft-calendar": {
        displayName: "Microsoft Calendar",
        tagline: "同步 Outlook / Microsoft 日历，让会议与参会者流入图谱。"
      },
      "apple-calendar": {
        displayName: "Apple Calendar",
        tagline: "即将推出 — 通过 CalDAV 同步 iCloud 日历。"
      }
    },
    replyTemplate: {
      eyebrow: "回复自动化",
      heading: "Gmail 回复模板",
      subtitle: "给 Agent 一个用户认可的模板库，邮件回复启用后只从这里选择。",
      newTemplate: "新建模板",
      emptyTitle: "还没有模板",
      enabledLabel: "允许 Agent 使用",
      enabledHint: "只有启用的模板会进入 Agent 选择范围。",
      requiresReviewLabel: "需要人工确认",
      requiresReviewHint: "在最终发送流程明确批准前，建议保持开启。",
      nameLabel: "名称",
      namePlaceholder: "询价邮件",
      descriptionLabel: "使用场景",
      descriptionPlaceholder: "当潜在客户询问价格或采购流程时使用。",
      subjectLabel: "主题",
      subjectPlaceholder: "Re: {{subject}}",
      bodyLabel: "正文",
      bodyPlaceholder: "Hi {{senderName}},\n\n感谢联系。我会查看后尽快跟进。\n\nBest,\n{{myName}}",
      variablesHint: "可用变量：{{senderName}}、{{senderEmail}}、{{subject}}、{{myName}}",
      create: "创建模板",
      creating: "创建中…",
      save: "保存模板",
      saving: "保存中…",
      saved: "模板已保存。",
      createError: "无法创建模板，请重试。",
      saveError: "无法保存模板，请重试。",
      loadError: "无法加载已保存模板。",
      updatedAt: (date) => `更新于 ${date}`
    },
    candidates: {
      eyebrow: "Inbox intelligence",
      heading: "Lead 候选",
      subtitle: "Agent 从邮箱中判断出的真实潜在客户，等你确认。",
      emptyTitle: "目前没有 lead 候选",
      emptyBody: "同步你的 Gmail 账号，Agent 会在这里列出判断为真实潜在客户的邮件。",
      confidence: (percent) => `置信度 ${percent}%`,
      draftReply: "起草回复",
      draftReplyPending: "起草中…",
      draftReplyCreated: "Gmail 草稿已创建。发送前请在 Gmail 中检查。",
      draftReplyError: "无法创建 Gmail 草稿，请重试。",
      draftReconnectRequired: "请重新连接 Gmail 以授权创建草稿。",
      confirm: "确认",
      confirmPending: "确认中…",
      confirmError: "无法确认此候选项，请重试。",
      dismiss: "忽略",
      dismissPending: "忽略中…",
      dismissError: "无法忽略此候选项，请重试。",
      needsFrame: "请先打开工作台并保存一张图谱画面，再确认候选项。",
      frameConflictRetry: "确认过程中图谱发生了变化，请刷新后重试。"
    }
  },
  schedule: {
    eyebrow: "只读议程",
    title: "日程",
    range: (from, to) => `${from} 至 ${to}`,
    items: (count) => `${count} 项`,
    signInToLoad: "登录后查看你的私密日程议程。",
    loading: "正在加载日程…",
    unavailable: "日程暂时无法使用。",
    calendarTitle: "日历事件",
    calendarEmpty: "此时间范围内没有日历事件。",
    untitledEvent: "未命名日历事件",
    timeUnavailable: "时间不可用",
    followUpsTitle: "未排期跟进",
    followUpsEmpty: "没有等待排期的跟进。",
    followUpRequested: (date) => `请求跟进于 ${date}`,
    remindersTitle: "待重联关系",
    remindersEmpty: "没有需要关注的冷却关系。",
    lastSeen: (date) => `最近联系 ${date}`,
    staleByDays: (days) => `已逾期 ${days} 天`,
    truncated: "此有界响应之外还有更多项目。",
    close: "关闭日程"
  },
  currentLeads: {
    eyebrow: "跟进管道",
    title: "当前",
    activeCount: (count) => `${count} 位在跟进`,
    emptyTitle: "当前还没有内容",
    emptyBody: "确认一个邮件 lead 候选，或将任意节点标记为当前，即可在这里追踪。",
    viewInGraph: "在图谱中查看",
    removeFromCurrent: "移出当前",
    sourceEmail: "来自邮件"
  },
  mcpConsent: {
    title: "授权 MCP 访问",
    description: "选择此 MCP 客户端可以访问你 Salesko 图谱的范围，然后继续以授予访问权限。",
    scopeGraphReadLabel: "读取你的图谱",
    scopeGraphReadHint: "搜索并读取人物、公司、事件和商机。",
    scopeCalendarReadLabel: "读取你的日程",
    scopeCalendarReadHint: "列出并搜索即将到来的日程事件。",
    scopeGraphWriteLabel: "提议图谱变更",
    scopeGraphWriteHint: "提交待人工审核的变更提议；不会自动应用任何变更。",
    continue: "继续",
    signInBody: "登录以授权此 MCP 客户端访问你的 Salesko 图谱。",
    invalidLinkHeading: "授权链接无效",
    invalidLinkBody: "此 MCP 授权链接缺失或无效，请从你的 MCP 客户端重新发起。",
    authNotConfiguredBody: "此 Salesko 部署尚未配置登录方式，因此无法授予 MCP 访问权限。",
    issuanceFailed: "无法授予 MCP 访问权限，请重试。"
  },
  meetingPreparation: {
    ariaLabel: "会议准备",
    title: "会议准备",
    prepareAction: "准备简报",
    prepareActionTitle: "为此事件生成会议简报",
    preparePending: "准备中…",
    idleBody: "为这场会议生成 AI 简报：目标、参会人、关系信号与建议问题。",
    loading: "正在准备会议简报…",
    successHeading: "会议简报",
    goalTitle: "目标",
    participantsTitle: "参会人与已知背景",
    knownContextEmpty: "除参会人外暂无更多背景信息。",
    relationshipSignalsTitle: "关系信号",
    relationshipSignalsEmpty: "你的图谱中暂无关系信号。",
    watchoutsTitle: "注意事项",
    watchoutsEmpty: "未识别到需要注意的事项。",
    suggestedQuestionsTitle: "建议提问",
    suggestedCloseTitle: "建议收尾",
    sourcesTitle: "来源",
    sourcesTruncated: "此有界响应之外还有更多来源。",
    sourceCalendarEvent: "日历事件",
    sourceCalendarAttendee: "日历参会人",
    sourceGraphEdge: "关系",
    sourceEvidence: "证据",
    sourceFrameProvenance: (frameId, acceptedAt) => `图谱帧 ${frameId} · 采纳于 ${acceptedAt}`,
    gapsTitle: "缺口",
    gapsEmpty: "未发现缺口——此简报证据完整。",
    gapNoGraphPersonMatch: (participantLabel) => `未在图谱中找到 ${participantLabel} 的匹配人物。`,
    gapNoRelationshipEvidence: "你的图谱中暂无可用的关系证据。",
    gapNoOpportunityEvidence: "你的图谱中暂无可用的商机证据。",
    gapContextTruncated: (collections) => `部分背景信息被截断：${collections}。`,
    gapOptionalCalendarDetailUnavailable: (detail) => `此日历事件缺少${detail}。`,
    calendarDetailLabels: {
      summary: "摘要",
      location: "地点",
      endAt: "结束时间",
      organizer: "组织者"
    },
    truncatedCollectionLabels: {
      calendarAttendees: "日历参会人",
      graphPeople: "图谱人物",
      graphContextNodes: "图谱背景",
      graphEdges: "图谱关系",
      opportunities: "商机",
      evidence: "证据"
    },
    errorEntitlement: "会议准备功能需要付费订阅，请升级后使用。",
    errorNotEligible: "此事件不符合生成会议简报的条件。",
    errorStaleEvent: "此日历事件已过期，请同步日历后重试。",
    errorModelError: "暂时无法生成简报，请重试。",
    errorGeneric: "无法准备此会议简报，请重试。"
  }
} satisfies Dictionary;
